import { closeModal } from "app/slices/modalSlice";

import { useTranslation } from "react-i18next";
import { Button, ControlledSelectGray, Input } from "components/Shared";
import { useDispatch } from "react-redux";
import { RFIDraft, RFIEntities } from "interfaces/router";

import { toast } from "sonner";
import { Controller, useForm } from "react-hook-form";
import { Send } from "lucide-react";
import useErrorListener from "hooks/useErrorListener";

import { RFIType } from "interfaces/enums";
import { useGetAdminEmailsQuery } from "api/institutional/onboarding_api";
import { useCreateRFIMutation } from "api/institutional/rfi_api";
import { DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useEffect } from "react";

interface SendRFI extends RFIDraft {
  destination_account_id?: string;
  kyb_id?: string;
  language: "en" | "es";
}

export const RequestInfoModal = ({ data }: { data: RFIEntities }) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const [mutateRFI, { error, isLoading }] = useCreateRFIMutation();

  const { destination_account_id, kyb_id, callback, order_router_id } = data;

  const { data: adminUserEmails } = useGetAdminEmailsQuery({});

  const handleClose = () => {
    dispatch(closeModal());
  };

  const {
    handleSubmit,
    register,
    control,
    reset,
    formState: { isValid },
  } = useForm<SendRFI>();

  useEffect(() => {
    if (order_router_id) {
      reset({
        language: "es",
        text_submission: t("Invoice needed"),
        document_type: RFIType.INVOICE,
        document_title: "",
        document_description: "",
      });
    }
  }, [order_router_id, reset, t]);

  useErrorListener({ error });

  const doSubmit = async (values: SendRFI) => {
    if (!destination_account_id && !kyb_id && !order_router_id) {
      return toast.error(t("destination account or kyb must be provided"));
    }

    if (values.document_title === undefined) {
      return toast.error(t("Please select a document title"));
    }

    if (values.document_type === undefined) {
      return toast.error(t("Please select a document type"));
    }

    if (values.decider === undefined) {
      return toast.error(t("Please select a decider"));
    }

    try {
      await mutateRFI({
        document_title: values.document_title?.trim(),
        document_description: values.document_description?.trim(),
        document_type: values.document_type,
        decider_id: values.decider?.trim(),
        destination_account_id: data.destination_account_id,
        kyb_id: data.kyb_id,
        language: values.language,
        order_router_id,
      }).unwrap();
      toast.success(t("RFI Sent"));
      callback?.();
      handleClose();
    } catch (e) {
      toast.error(t("RFI Failed to Send"));
    }
  };

  const DocumentTypes = () => {
    return [
      RFIType.INVOICE,
      RFIType.CONTRACT,
      RFIType.BUSINESS_FORMATION,
      RFIType.PROOF_OF_ADDRESS,
      RFIType.RECEIPT,
      RFIType.OTHER,
    ];
  };

  return (
    <DialogContent className="max-h-[90vh] overflow-y-auto">
      <DialogTitle>{t("Send new RFI")}</DialogTitle>
      <form onSubmit={handleSubmit(doSubmit)} className="flex gap-2 flex-col">
        <span className="text-sm text-gray-500">
          {t(
            "Fill out the details of the RFI, this will send an email to the broker/broker group",
          )}
        </span>
        <Input
          placeholder={t("Document Title")}
          {...register("document_title", { required: true })}
        />
        <Controller
          name="document_description"
          control={control}
          render={({ field }) => (
            <Textarea
              rows={4}
              placeholder={t("Document Description (Optional)")}
              {...field}
            />
          )}
        />
        <Controller
          name="document_type"
          rules={{
            required: true,
          }}
          control={control}
          render={(renderProps) => (
            <ControlledSelectGray
              placeholder={t("Select an RFI Type")}
              renderProps={renderProps}
              options={DocumentTypes()}
            />
          )}
        />
        <Controller
          name="decider"
          rules={{
            required: true,
          }}
          control={control}
          render={(renderProps) => (
            <ControlledSelectGray
              placeholder={t("Select a Decider")}
              renderProps={renderProps}
              options={adminUserEmails || []}
            />
          )}
        />
        <Controller
          name="language"
          rules={{
            required: true,
          }}
          control={control}
          render={(renderProps) => (
            <ControlledSelectGray
              placeholder={t("Select a language")}
              renderProps={renderProps}
              options={["en", "es"]}
            />
          )}
        />
        <Button type="submit" disabled={!isValid || isLoading}>
          <Send />
          {t("Send")}
        </Button>
      </form>
    </DialogContent>
  );
};

export default RequestInfoModal;
